import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { ICellRendererAngularComp } from '@ag-grid-community/angular';
import { ColDef, ICellRendererParams } from '@ag-grid-community/core';
import { IconSize } from '@panaya/ui/p-icon';
import { ColorName } from '@panaya/ui/styles-to-code';
import { LabelTypeEnum } from '@frontend-site/ui/components';

@Component({
    selector: 'status-sub-label-renderer',
    template: `
        <div class="icon-text-renderer" fxLayoutGap="4px" [ngClass]="{ 'right-align': iconOnRight, 'left-align': !iconOnRight }">
            <p-icon
                *ngIf="icon"
                [name]="icon"
                [color]="iconColor"
                [size]="size"
                [matTooltip]="iconTooltip || tooltipValue"
                [ngClass]="{ 'right-icon': iconOnRight }"
            ></p-icon>
            <p-status-sub-label
                [label]="label"
                [type]="labelType"
                [matTooltip]="tooltipValue !== value ? tooltipValue : ''"
                [matTooltipShowDelay]="1000"
            ></p-status-sub-label>
        </div>
    `,
    styleUrls: ['./status-sub-label-renderer.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class StatusSubLabelRendererComponent implements ICellRendererAngularComp {
    public label!: string;
    public labelType?: LabelTypeEnum;
    public value!: string;
    public icon!: string;
    public iconTooltip!: string;
    public iconColor!: ColorName;
    public hideText = false;
    public size: IconSize = 'md';
    public tooltipValue!: string;
    public showOnlyWhenEllipsis!: boolean;
    public iconOnRight = false;

    constructor(private cd: ChangeDetectorRef) {}

    agInit(params: StatusSubLabelRendererParams): void {
        this.extractParams(params);
        const colDef = params.colDef as ColDef;
        const inputData = colDef.cellRendererParams || params;
        this.value = params.getValue?.();

        if (inputData?.iconsMap) {
            const technicalValue = inputData?.iconField
                ? this.getIconFieldValue(params.data, inputData.iconField)
                : params.data[colDef.field as string];
            const iconObject = inputData?.iconsMap.get(technicalValue);
            this.icon = iconObject?.icon;
            this.iconColor = iconObject?.color;
            this.size = iconObject?.size || 'md';
            this.iconTooltip = iconObject?.tooltip;
        } else {
            this.icon = inputData?.getIcon ? inputData.getIcon(params.node.data) : inputData.icon;
            this.size = inputData?.iconSize || 'md';
        }
        this.hideText = inputData?.hideText;
        this.tooltipValue = inputData?.getTooltipValue ? inputData.getTooltipValue(params.node.data) : this.value;
        this.showOnlyWhenEllipsis = inputData?.showTooltipAlways ? !inputData.showTooltipAlways : true;
        this.iconOnRight = inputData.iconOnRight || false;
    }

    refresh(params: StatusSubLabelRendererParams): boolean {
        this.extractParams(params);
        this.cd.detectChanges();
        return true;
    }

    private extractParams(params: StatusSubLabelRendererParams): void {
        this.label = typeof params.label === 'function' ? params.label(params.data) : params.label || params.getValue?.();
        this.labelType = typeof params.labelType === 'function' ? params.labelType(params.data) : params.labelType;
    }

    private getIconFieldValue(data: Record<string, any>, field: string): string {
        return field.split('.').reduce<any>((value, key) => value[key], data);
    }
}

interface StatusSubLabelRendererParams extends ICellRendererParams {
    label: Function | string;
    labelType?: Function | LabelTypeEnum;
    icon?: string;
}
